<template>
  <div class="px-3">
    <template v-for="(item, index) in shippedItems" :key="item.id">
      <ProductRow 
        :item="item"
        :productRoute="productRoute"
        :margin="`${index+1 === items.length ? 'mb-0' : 'mb-3'}`" 
        :sku="item.product_sku">
        <div class="qty">
          <span class="text-secondary">{{ formatDollars(item.bundleCost || item.cost_item) }} x {{ item.qty }}</span>
        </div>
        <div v-if="item.from_inventory && $page.props.auth.user.role == 'mtd'">
          <jet-button v-if="!item.inventory_delivered" icon="fas fa-box" :disable="formInventory.processing" class="btn-short" @click="markDelivered(item)">Mark Item Delivered</jet-button>
          <p v-else class="text-secondary mb-0">Item Provided from Inventory</p>
        </div>
        <div v-else>
          <p v-if="item.costTotalShip > 0" class="mb-0 text-secondary">
            Flat Shipping: {{ formatDollars(item.costTotalShip) }}
          </p>
          <div v-else>
            <template v-if="item.from_inventory">
              <p v-if="!item.inventory_delivered" class="mb-0 text-secondary">MTD to Provide From Inventory</p>
              <p v-else class="mb-0 text-secondary">MTD Provided From Inventory</p>
            </template>
            <p v-else class="mb-0 text-secondary">MTN Shipping</p>
          </div>
        </div>
        <p v-if="item.qty_shipped >= 1 && !(item.qty === item.qty_shipped)" class="mb-0 font-weight-bold text-dark">
          <i class="fas fa-truck-moving mr-1"></i>
          {{item.qty - item.qty_shipped}} items on backorder
        </p>
        <template v-slot:priceInfo>
          <div v-if="item.canceled" class="d-flex flex-column align-items-end">
            <p class="strikethrough text-secondary font-weight-bold mb-0">{{ formatDollars((item.bundleCost || item.cost_item) * item.qty) }}</p>
            <p class="mb-0 text-secondary">Canceled</p>
          </div>
          <div v-else class="mb-0">{{ formatDollars((item.bundleCost || item.cost_item) * item.qty) }}</div>
        <div class="clearfix" v-if="$page.props.auth.user.role == 'mtd' && order.user.id != $page.props.auth.user.id">
          <jet-button
            v-if="!item.tu_serial"
            btnStyle="dark"
            class="btn-sm mt-1" 
            @click="item.tu_serial = ['']"
          >
            Enter Serial # Information
          </jet-button>

          <template v-else>
            <div v-for="(serial, index) in item.tu_serial" :key="`${serial}-${index}`" class="mt-1">
              <input 
                @change="updateSerial($event, item, index)"
                type="text"
                placeholder="Enter Serial #"
                class="form-control serial-input form-control-sm"
                :value="serial"
              />
            </div>
          </template>
        </div>
        </template>
      </ProductRow>
    </template>
  </div>

  <div class="bg-lighter pt-4 pb-2 border-bottom px-3" v-if="backorderItems.length">
    <div class="col-12 col-lg-7 px-0 ml-auto">
      <ul class="list-unstyled mb-0">
        <template v-for="lineItem in lineItems">
          <li class="d-flex justify-content-between">
            <strong>{{ lineItem.description }}<span v-if="lineItem.qty > 1"> ({{ lineItem.qty }})</span></strong>
            <span class="text-secondary">{{ formatDollars(lineItem.total) }}</span>
          </li>
        </template>
        <li v-if="order.shipping_cost > 0" class="d-flex justify-content-between"><strong>Shipping</strong><span class="text-secondary">{{ formatDollars(order.shipping_cost) }}</span></li>
        <li v-if="order.flatShippingCost > 0" class="d-flex justify-content-between"><strong>Flat Shipping</strong><span class="text-secondary">{{ formatDollars(order.flatShippingCost) }}</span></li>
        <li v-if="order.sales_tax_amount > 0" class="d-flex justify-content-between"><strong>Sales Tax</strong><span class="text-secondary">{{ formatDollars(order.sales_tax_amount) }}</span></li>
        <li class="d-flex justify-content-between align-items-end"><strong>Total Billed:</strong><h5 class="text-success mb-0">{{ formatDollars(totalBilled) }}</h5></li>
      </ul>
    </div>
  </div>

  <div class="px-3">
    <div v-if="currentBackorderItems.length">
      <p class="badge badge-primary py-2 px-4 mb-1 rounded-0">On backorder</p>
      <template v-for="(item, index) in currentBackorderItems" :key="item.id">
        <ProductRow 
          :item="item"
          :productRoute="productRoute"
          :margin="`${index+1 === currentBackorderItems.length ? 'mb-0' : 'mb-3'}`" 
          :sku="item.product_sku">
          <template v-slot:headerContent>
            <div class="col-4">
              <p class="badge badge-primary pt-1 ml-2 float-right">
                <i class="fas fa-clock mr-1"></i>
                {{ getBackorderEta(item) }}
              </p>
            </div>
          </template>
          <div class="qty">
            <p class="mb-1 text-secondary" v-if="!item.discountApplied">${{ formatDollars((item.bundleCost || item.cost_item)) }} x {{ item.qty }}</p>
          </div>
          <div>
            <p v-if="item.costTotalShip > 0" class="mb-0 text-secondary">
              Flat Shipping: {{ formatDollars(item.costTotalShip) }}
            </p>
            <div v-else>
              <template v-if="item.from_inventory">
                <p v-if="!item.inventory_delivered" class="mb-0 text-secondary">MTD to Provide From Inventory</p>
                <p v-else class="mb-0 text-secondary">MTD Provided From Inventory</p>
              </template>
              <p v-else class="mb-0 text-secondary">MTN Shipping</p>
            </div>
          </div>
          <template v-slot:priceInfo>
            <div v-if="item.canceled" class="d-flex flex-column align-items-end">
              <p class="strikethrough text-secondary font-weight-bold mb-0">{{ formatDollars((item.bundleCost || item.cost_item) * item.qty) }}</p>
              <p class="mb-0 text-secondary">Canceled</p>
            </div>
            <h5 class="mb-0 text-success" v-else>{{ formatDollars((item.bundleCost || item.cost_item) * item.qty) }}</h5>
          <div class="clearfix" v-if="$page.props.auth.user.role == 'mtd' && order.user.id != $page.props.auth.user.id">
            <jet-button
              v-if="!item.tu_serial"
              btnStyle="dark"
              class="btn-sm mt-1" 
              @click="item.tu_serial = ['']"
            >
              Enter Serial # Information
            </jet-button>

            <template v-else>
              <div v-for="(serial, index) in item.tu_serial" :key="`${serial}-${index}`" class="mt-1">
                <input 
                  @change="updateSerial($event, item, index)"
                  type="text"
                  placeholder="Enter Serial #"
                  class="form-control serial-input form-control-sm"
                  :value="serial"
                />
              </div>
            </template>
          </div>
          </template>
        </ProductRow>
      </template>
    </div>
  </div>

  <div class="bg-lighter pt-4 pb-2 border-bottom px-3">
    <div class="col-12 col-lg-7 px-0 ml-auto">
      <ul class="list-unstyled" v-if="backorderItems.length == 0">
        <li class="d-flex justify-content-between"><strong>Subtotal</strong><span>{{ formatDollars(order.subtotal) }}</span></li>
        <template v-for="lineItem in lineItems">
          <li class="d-flex justify-content-between">
            <strong>{{ lineItem.description }}<span v-if="lineItem.qty > 1"> ({{ lineItem.qty }})</span></strong>
            <span class="text-secondary">{{ formatDollars(lineItem.total) }}</span>
          </li>
        </template>
        <li v-if="order.shipping_cost > 0" class="d-flex justify-content-between"><strong>Shipping</strong><span class="text-secondary">{{ formatDollars(order.shipping_cost) }}</span></li>
        <li v-if="order.flatShippingCost > 0" class="d-flex justify-content-between"><strong>Flat Shipping</strong><span class="text-secondary">{{ formatDollars(order.flatShippingCost) }}</span></li>
        <li v-if="order.sales_tax_amount > 0" class="d-flex justify-content-between"><strong>Sales Tax</strong><span class="text-secondary">{{ formatDollars(order.sales_tax_amount) }}</span></li>
        <slot></slot>
        <li class="d-flex justify-content-between align-items-end mt-2 pt-2 border-top"><h5><strong>Total</strong></h5><h5 class="font-weight-bold mb-0">{{ formatDollars(orderTotal()) }}</h5></li>
      </ul>
      <ul class="list-unstyled" v-else>
        <li class="d-flex justify-content-between"><strong>Pending Charges</strong><span class="text-secondary">{{ formatDollars(orderTotal() - totalBilled) }}</span></li>
        <slot></slot>
        <li class="d-flex justify-content-between align-items-end mt-2 pt-2 border-top"><h5><strong>Total</strong></h5><h5 class="font-weight-bold mb-0">{{ formatDollars(orderTotal()) }}</h5></li>
      </ul>

      <p v-if="totalDiscount() > 0" class="text-small lh-small text-right my-2 text-secondary">You saved {{ formatDollars(totalDiscount()) }}</p>
    </div>
  </div>
</template>

<script>
  import ProductRow from '@/Components/Shop/ProductRow'
  import moment from 'moment'

  export default {
    components: {
      ProductRow
    },
    props: {
      items: Array,
      order: Object,
      productRoute: String,
      lineItems: {
        type: Array,
        default: [],
      },
      totalBilled: Number,
      isWholesale: Boolean,
      backorderItems: Array,
    },
    data() {
      return {
        formInventory: this.$inertia.form({
          _method: 'put',
          item_id: null,
          tu_serial_info: null
        }),
        shippedItems: this.items.filter(item => {
          const fromTruck = !this.isWholesale && item.from_inventory
          return !item.show_as_backorder || fromTruck
        }),
        itemsOnBackorder: this.items.filter(item => {
          const fromTruck = !this.isWholesale && !item.from_inventory
          return this.isWholesale ? item.show_as_backorder : item.show_as_backorder && fromTruck
        }),
      }
    },
    mounted() {
      this.initItems()
    },
    created() {
      this.moment = moment
    },
    updated() {
      this.initItems()
    },
    computed: {
      currentBackorderItems() {
        return this.backorderItems.length ? this.backorderItems : this.itemsOnBackorder
      }
    },
    methods: {
      initItems() {
        this.items.forEach(orderItem => {
          if (orderItem.tu_serial_info) {
            orderItem.tu_serial = JSON.parse(orderItem.tu_serial_info)
            if (orderItem.qty > orderItem.tu_serial.length) {
              orderItem.tu_serial[orderItem.tu_serial.length] = '';
            }
          } else {
          }
        })
      },
      orderTotal() {
        return this.order.total
      },
      getBackorderEta(item) {
        if (this.backorderItems.length) {
          return 'Expected on ' + this.moment.utc(item.backorder_eta).format('MM/DD/YY')
        }
        else {
          if (item.backorder_eta && item.backorder_eta % 7 === 0) 
            return 'Expected to Ship: ' + item.backorder_eta/7 + ' weeks'
          else
            return 'Expected to Ship: ' + item.product_backorder_eta + ' days'
        }
      },
      discountedTotal() {
        let total = 0
        this.items.forEach(item => total += item.cost_item * item.qty)
        return total
      },
      totalDiscount() {
        return this.order.subtotal_before_discounts - this.order.subtotal
      },
      markDelivered(item) {
        this.formInventory.item_id = item.id
        this.formInventory.post(this.route('mtd.orders.markdelivered', this.order.id))
      },
      updateSerial(e, item, index) {
        item.tu_serial[index] = e.target.value
        this.formInventory.item_id = item.id
        this.formInventory.tu_serial_info = JSON.stringify(item.tu_serial)
        this.formInventory.post(this.route('mtd.orders.setserial', this.order.id))
      }
    }
  }
</script>
